import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDebounce } from '@uidotdev/usehooks';
import { Command } from 'cmdk';
import { useCallback, useMemo } from 'react';

import { CommandBarPriority } from '#components/command-bar/command-bar-types.js';
import { AddCommandBarProvider } from '#components/command-bar/components/add-command-bar-provider.js';
import { CommandBarTypedItem } from '#components/command-bar/components/command-bar-typed-item.js';
import { useTRPC } from '#src/trpc.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { PACKAGE_SEARCH_PREFIX, PACKAGE_SEARCH_PROVIDER_ID } from './package-search-types.js';

import type { PackageSearchProviderProps } from './package-search-types.js';

/**
 * Component that registers the package search provider with the command bar globally.
 * Only renders when the current user has `manage` permission on the `package` subject.
 *
 * Use the `p:` prefix in the command bar to search for packages by name.
 * No data is fetched until the user activates the search prefix.
 */
export function RegisterPackageSearchProvider() {
	const ability = useAbilityContext();
	const canManagePackages = ability.can('manage', 'package');

	const getRelevance = useCallback((search: string) => {
		if (search.startsWith(PACKAGE_SEARCH_PREFIX)) {
			return CommandBarPriority.VeryHigh;
		}
		return CommandBarPriority.Low;
	}, []);

	const provider = useMemo(
		() => ({
			id: PACKAGE_SEARCH_PROVIDER_ID,
			component: PackageSearchResults,
			getRelevance,
			searchPrefix: PACKAGE_SEARCH_PREFIX,
			props: {},
		}),
		[getRelevance],
	);

	if (!canManagePackages) {
		return null;
	}

	return <AddCommandBarProvider<PackageSearchProviderProps> provider={provider} />;
}

function PackageSearchResults({ search, onClose }: PackageSearchProviderProps) {
	const navigate = useNavigate();
	const trpc = useTRPC();

	const term = useMemo(
		() =>
			search.startsWith(PACKAGE_SEARCH_PREFIX) ? search.slice(PACKAGE_SEARCH_PREFIX.length).toLowerCase().trim() : '',
		[search],
	);
	const debouncedTerm = useDebounce(term, 200);

	const { data } = useQuery(
		trpc.package.search.queryOptions(
			{ textQuery: debouncedTerm, limit: 50, sort: [{ field: 'manufacturerPartNumber', direction: 'asc' }] },
			{ enabled: debouncedTerm.length > 2 },
		),
	);

	const results = data?.pkgs ?? [];

	if (results.length === 0) {
		return null;
	}
	return (
		<Command.Group heading='Packages'>
			{results.map((pkg) => (
				<Command.Item
					key={pkg.id}
					value={`${PACKAGE_SEARCH_PREFIX}${pkg.id}`}
					keywords={[pkg.manufacturerPartNumber, pkg.packageFamilyName, pkg.manufacturerName, 'package', 'part', 'mpn']}
					onSelect={() => {
						void navigate({
							to: '/package-store/$packageFamilyId/packages/$packageId',
							params: { packageFamilyId: pkg.packageFamilyId, packageId: pkg.id },
						});
						onClose();
					}}
				>
					<CommandBarTypedItem type={PACKAGE_SEARCH_PREFIX} name={pkg.manufacturerPartNumber}>
						{(pkg.packageFamilyName || pkg.manufacturerName) && (
							<span className='text-sm font-normal text-gray-a11'>
								{[pkg.manufacturerName, pkg.packageFamilyName].filter(Boolean).join(' · ')}
							</span>
						)}
					</CommandBarTypedItem>
				</Command.Item>
			))}
		</Command.Group>
	);
}
